import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './YfDataStatus.css';
import { Button, Spinner, Toggle } from '../../../components/widgets';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../../components/foundations/Icon';
import useYfAuditStatus from '../../hooks/useYfAuditStatus';
import useElapsed from '../../hooks/useElapsed';
import ConnectorRow from './ConnectorRow';
import { downloadAuditCsv } from '../../yfDataStatusCsv';
import {
    AUDIT_METRICS,
    AUDIT_STATUS,
    formatAuditDay,
    formatClock,
    formatElapsed,
    groupRows,
    ROW_GROUP,
    yfTranslationKey,
} from '../../yfDataStatusUtils';

const TOGGLE_LABEL_STYLE = { fontSize: 12, color: 'var(--gray600)', marginRight: '8px', whiteSpace: 'nowrap' };

// DISABLED and INACTIVE are the drawer below the table.
const SUMMARY_GROUPS = [ROW_GROUP.UNAVAILABLE, ROW_GROUP.MISSING, ROW_GROUP.GAP, ROW_GROUP.UNCHECKED, ROW_GROUP.MATCHED];
const TITLED_GROUPS = [ROW_GROUP.UNCHECKED, ROW_GROUP.MATCHED];

// Its own component so the per-second clock does not re-render the table.
function BulkProgress({ bulk, onStop, translate }) {
    const elapsedMs = useElapsed(bulk.startedAt);

    return (
        <div className={css.progress}>
            <div className={css.progressTrack}>
                <div className={css.progressFill} style={{ width: `${(bulk.done / bulk.total) * 100}%` }} />
            </div>
            <span className={css.progressLabel}>
                {translate(yfTranslationKey('PROGRESS'), { done: bulk.done, total: bulk.total })}
                <span className={css.progressElapsed}>{formatElapsed(elapsedMs)}</span>
            </span>
            <button type="button" className={css.linkButton} onClick={onStop}>
                {translate(yfTranslationKey('STOP_BUTTON'))}
            </button>
        </div>
    );
}

BulkProgress.propTypes = {
    bulk: PropTypes.shape({
        total: PropTypes.number.isRequired,
        done: PropTypes.number.isRequired,
        startedAt: PropTypes.number.isRequired,
    }).isRequired,
    onStop: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

function UnauditableDrawer({ rows, translate }) {
    const [open, setOpen] = useState(false);

    return (
        <div className={css.drawer}>
            <button type="button" className={css.drawerHeader} onClick={() => setOpen(!open)} aria-expanded={open}>
                <Icon
                    name={open ? 'chevron-up' : 'chevron-down'}
                    type={IconTypes.TABLER}
                    size={IconSizes.X_SMALL}
                    variant={IconVariants.LIGHT}
                    interactive={false}
                />
                <span className={css.drawerText}>
                    <span className={css.drawerTitle}>
                        {translate(yfTranslationKey('UNAUDITABLE_TITLE'), { count: rows.length })}
                    </span>
                    <span className={css.drawerHint}>{translate(yfTranslationKey('UNAUDITABLE_HINT'))}</span>
                </span>
            </button>
            {open && (
                <div className={css.drawerRows}>
                    {rows.map(connector => {
                        let reason = null;
                        if (connector.status === AUDIT_STATUS.INACTIVE) {
                            reason = connector.inactive_since
                                ? translate(yfTranslationKey('UNAUDITABLE_REASONS.INACTIVE'), {
                                      date: new Date(`${connector.inactive_since}T00:00:00Z`).toLocaleDateString(
                                          undefined,
                                          {
                                              timeZone: 'UTC',
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                          }
                                      ),
                                  })
                                : translate(yfTranslationKey('UNAUDITABLE_REASONS.INACTIVE_NO_DATE'));
                        } else if (connector.unsupported_reason) {
                            reason = translate(
                                yfTranslationKey(`UNAUDITABLE_REASONS.${connector.unsupported_reason.toUpperCase()}`)
                            );
                        }
                        return (
                            <div key={connector.uan_id} className={css.drawerRow}>
                                <span className={css.drawerName}>{connector.display_name}</span>
                                <span className={css.drawerLabel}>{connector.label}</span>
                                {reason && <span className={css.drawerReason}>{reason}</span>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

UnauditableDrawer.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    translate: PropTypes.func.isRequired,
};

function YfDataStatus({ translate = txt => txt, adminMode = false }) {
    const {
        snapshot,
        visibleRows,
        archivedCount,
        calculableCount,
        loadError,
        calculationErrors,
        isLoading,
        startedAt,
        queuedUanIds,
        bulk,
        isCalculatingAny,
        showArchived,
        setShowArchived,
        calculate,
        calculateAll,
        stopCalculateAll,
    } = useYfAuditStatus({ translate });

    if (isLoading) {
        return <Spinner show expanded />;
    }

    if (!snapshot) {
        return <div className={css.error}>{loadError || translate(yfTranslationKey('LOAD_ERROR'))}</div>;
    }

    const groups = groupRows(visibleRows);
    const counts = Object.keys(groups).reduce((totals, group) => ({ ...totals, [group]: groups[group].length }), {});
    const unauditable = [...(groups[ROW_GROUP.INACTIVE] || []), ...(groups[ROW_GROUP.DISABLED] || [])];

    const renderRow = connector => (
        <ConnectorRow
            key={connector.uan_id}
            connector={connector}
            isCalculating={connector.uan_id in startedAt}
            isQueued={queuedUanIds.includes(connector.uan_id)}
            startedAt={startedAt[connector.uan_id] || null}
            error={calculationErrors[connector.uan_id]}
            onCalculate={calculate}
            timeZone={snapshot.org_timezone}
            translate={translate}
            adminMode={adminMode}
        />
    );

    return (
        <div className={css.page}>
            <div className={css.header}>
                <div className={css.headerText}>
                    <div className={css.day}>{formatAuditDay(snapshot.audit_date)}</div>
                    <div className={css.orgSchedule}>
                        {translate(
                            yfTranslationKey(snapshot.audit_scheduled_at ? 'ORG_SCHEDULE' : 'ORG_SCHEDULE_NO_AUDIT'),
                            {
                                timezone: snapshot.org_timezone,
                                expectedAt: formatClock(snapshot.expected_data_at, snapshot.org_timezone),
                                expectedAtUtc: formatClock(snapshot.expected_data_at, 'UTC'),
                                auditAt: formatClock(snapshot.audit_scheduled_at, snapshot.org_timezone),
                            }
                        )}
                    </div>
                </div>
                <div className={css.headerActions}>
                    <Button
                        type="secondary"
                        onClick={() => downloadAuditCsv(visibleRows, snapshot.audit_date)}
                        disabled={!visibleRows.length}
                    >
                        {translate(yfTranslationKey('EXPORT_CSV_BUTTON'))}
                    </Button>
                    <Button type="primary" onClick={calculateAll} disabled={isCalculatingAny || !calculableCount}>
                        {translate(yfTranslationKey('CALCULATE_ALL_BUTTON'))}
                    </Button>
                </div>
            </div>

            {bulk && <BulkProgress bulk={bulk} onStop={stopCalculateAll} translate={translate} />}

            {loadError && <div className={css.inlineError}>{loadError}</div>}

            <div className={css.summaryBar}>
                <div className={css.summary}>
                    {SUMMARY_GROUPS.filter(group => counts[group]).map(group => (
                        <span key={group} className={css.summaryItem}>
                            <span className={`${css.summaryDot} ${css[`dot_${group}`]}`} />
                            <span className={css.summaryCount}>{counts[group]}</span>
                            {translate(yfTranslationKey(`SUMMARY.${group.toUpperCase()}`))}
                        </span>
                    ))}
                </div>
                <Toggle
                    label="STATIC.PAGES.DATA_SOURCES.TOGGLE_ARCHIVED_UANS"
                    labelPosition="start"
                    checked={showArchived}
                    onToggle={() => setShowArchived(!showArchived)}
                    disabled={isCalculatingAny || !archivedCount}
                    ariaLabel={translate('STATIC.PAGES.DATA_SOURCES.TOGGLE_ARCHIVED_UANS')}
                    styleOverride={{ labelStyle: TOGGLE_LABEL_STYLE }}
                />
            </div>

            <div className={css.tableScroll}>
                <div className={css.table}>
                    <div className={css.tableHeader}>
                        <div>{translate(yfTranslationKey('CONNECTOR_COLUMN'))}</div>
                        {AUDIT_METRICS.map(metric => (
                            <div key={metric} className={css.metricHeader}>
                                {translate(yfTranslationKey(`METRICS.${metric.toUpperCase()}`))}
                            </div>
                        ))}
                        <div>{translate(yfTranslationKey('TIMES_COLUMN'))}</div>
                    </div>

                    {(groups[ROW_GROUP.UNAVAILABLE] || []).map(renderRow)}
                    {(groups[ROW_GROUP.MISSING] || []).map(renderRow)}
                    {(groups[ROW_GROUP.GAP] || []).map(renderRow)}

                    {TITLED_GROUPS.filter(group => counts[group]).map(group => (
                        <React.Fragment key={group}>
                            <div className={`${css.sectionHeader} ${css[`section_${group}`] || ''}`}>
                                {translate(yfTranslationKey(`SECTIONS.${group.toUpperCase()}`), {
                                    count: counts[group],
                                })}
                            </div>
                            {groups[group].map(renderRow)}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {!visibleRows.length && <div className={css.empty}>{translate(yfTranslationKey('EMPTY'))}</div>}

            {!!unauditable.length && <UnauditableDrawer rows={unauditable} translate={translate} />}

            {!showArchived && !!archivedCount && (
                <div className={css.footnote}>
                    {translate(yfTranslationKey('ARCHIVED_FOOTNOTE'), { count: archivedCount })}
                </div>
            )}
        </div>
    );
}

YfDataStatus.propTypes = {
    translate: PropTypes.func,
    adminMode: PropTypes.bool,
};

export default YfDataStatus;
